import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSkeleton } from "../../../hooks/useSkeleton";

import {
    Button,
    MuiThemeProvider,
    TextField,
    createMuiTheme,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import {
    Card,
    CardBody,
    CardHeader
} from "../../../../_metronic/_partials/controls";
import { getCountries } from "../../../../api/country";
import { getAllActiveCustomers } from "../../../../api/customer";
import { getAllActiveDevelopmentStates } from "../../../../api/developmentStatus";
import { getLocalidades } from "../../../../api/localidad";
import { deleteProject, getProjectById, postProject, updateProject } from "../../../../api/project";
import { getProvincias } from "../../../../api/provincia";
import { getAllActiveTechnologies } from "../../../../api/technology";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { getNonEmpty } from '../../../../utils/helpers'

// Create theme for delete button (red)
const theme = createMuiTheme({
    palette: {
        secondary: {
            main: "#F64E60",
        },
    },
});

function getDataCustomers(customers) {
    return customers
        .filter((customer) => customer.role.includes("seller"))
        .map((customer) => ({
            fullName: customer.fullName,
            _id: customer._id
        }));
}

function getDataTechnologies(technologies) {
    return technologies
        .map((technology) => ({
            title: getNonEmpty(technology.title),
            _id: technology._id
        }));
}

function getDataDevStatus(devStatus) {
    return devStatus
        .map((devStat) => ({
            title: getNonEmpty(devStat.title),
            _id: devStat._id
        }));
}


function getEmptyProject() {
    return {
        title: "",
        description: "",
        technology: "",
        country: "",
        province: "",
        city: "",
        developmentStatus: "",
        sizeMW: null, // Number
        status: "published",
        seller: ""
    }
}

function getEmptySelected() {
    return {
        _id: ""
    }
}


function TextSummary({ project, setProject }) {
    return (
        <TextField
            value={project.description}
            onChange={(e) =>
                setProject(prev => ({ ...prev, description: e.target.value }))
            }
            label="Description"
            multiline
            minRows={5}
            maxRows={8}
            variant="outlined"
            fullWidth
            margin="normal"
            inputProps={{
                style: {
                    overflowY: 'auto',
                },
            }}
            required
        />
    );
}

export default function EditProjectsPage() {
    const history = useHistory();
    const {
        isLoading: isLoadingData,
        disableLoading: disableLoadingData,
        ContentSkeleton
    } = useSkeleton();
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const projectId = useParams().id;
    const [project, setProject] = useState(getEmptyProject())

    const [sellers, setSellers] = useState([])
    const [technologies, setTechnologies] = useState([])
    const [countries, setCountries] = useState([])
    const [provinces, setProvinces] = useState([])
    const [cities, setCities] = useState([])
    const [developmentStates, setDevelopmentStates] = useState([])

    const [selectedCountry, setselectedCountry] = useState(getEmptySelected())
    const [selectedProvince, setselectedProvince] = useState(getEmptySelected())

    // const user = useSelector((store) => store.authentication?.user, shallowEqual);
    // const userCompany = user?.seller;


    useEffect(() => {
        getCountries()
            .then((res) => { setCountries(res.data) })
            .catch((error) => { alertError({ error: error, customMessage: "Could not get countries." }); })

        getLocalidades()
            .then((res) => { setCities(res.data) })
            .catch((error) => { alertError({ error: error, customMessage: "Could not get cities." }); })

        getProvincias()
            .then((res) => { setProvinces(res.data) })
            .catch((error) => { alertError({ error: error, customMessage: "Could not get provinces." }); })

        getAllActiveTechnologies()
            .then((res) => { setTechnologies(getDataTechnologies(res.data)) })
            .catch((error) => { alertError({ error: error, customMessage: "Could not get technologies." }); })

        getAllActiveDevelopmentStates()
            .then((res) => { setDevelopmentStates(getDataDevStatus(res.data)) })
            .catch((error) => { alertError({ error: error, customMessage: "Could not get development states." }); })

        getAllActiveCustomers("seller")
            .then((res) => { setSellers(getDataCustomers(res.data)) })
            .catch((error) => { alertError({ error: error, customMessage: "Could not get customers." }); })

        if (!projectId) {
            disableLoadingData();
            return;
        }

        getProjectById(projectId)
            .then((res) => {
                setProject(res.data);
                setselectedCountry(res.data.country)
                setselectedProvince(res.data.province)
                disableLoadingData();
            })
            .catch((error) => {
                alertError({ error: error, customMessage: "Could not get project." });
                disableLoadingData();
            });
    }, [projectId, disableLoadingData, history]);


    const handleChange = (field) => (event) => {
        setProject({ ...project, [field]: event.target.value });
    };

    function saveProject() {
        if (projectId) {
            updateProject(projectId, project).then(res => {
                alertSuccess({ title: 'Saved!', customMessage: 'Changes successfully saved.' })
                history.push('/projects')
            }).catch(error => {
                alertError({ error: error, customMessage: 'Could not save changes.' })
            })
        }
        else {
            postProject(project)
                .then((res) => {
                    alertSuccess({ title: "Saved!", customMessage: "Project successfully created." });
                    history.push("/projects");
                })
                .catch((error) => {
                    alertError({ error: error, customMessage: "Could not save the project." });
                });
        }
    }

    if (isLoadingData)
        return <ContentSkeleton />;

    return (
        <>
            <Card>
                <CardHeader title="Create new project" />
                <CardBody>
                    <div className="d-flex align-item-center">
                        <div className="col-5">
                            <TextField
                                label="Title"
                                value={project.title}
                                onChange={handleChange("title")}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                required
                            />
                        </div>
                        <div className="col-3" />
                        <div className="col-4">
                            <Autocomplete
                                id='Sellers'
                                options={sellers}
                                value={project.seller || null}
                                getOptionLabel={(option) => option.fullName}
                                onChange={(_, newValue) => {
                                    setProject({
                                        ...project,
                                        seller: newValue
                                    })
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Seller'
                                        margin="normal"
                                        variant="outlined"
                                        required
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className="d-flex align-item-center">
                        <div className="col-12">
                            <TextSummary project={project} setProject={setProject} />
                        </div>
                    </div>


                    <div className="d-flex align-item-center">
                        <div className="col-4">
                            <Autocomplete
                                id='Countries'
                                options={countries}
                                value={project.country || null}
                                getOptionLabel={(option) => option.name}
                                onChange={(_, newValue) => {
                                    if (!newValue)
                                        setselectedCountry(getEmptySelected());
                                    else
                                        setselectedCountry(newValue);
                                    setProject({
                                        ...project,
                                        country: newValue,
                                        province: "",
                                        city: ""
                                    })

                                    setselectedProvince(getEmptySelected())
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Country'
                                        margin="normal"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </div>
                        <div className="col-4">
                            <Autocomplete
                                id='Provinces'
                                options={provinces.filter(item => item.pais === selectedCountry._id)}
                                value={project.province || null}
                                getOptionLabel={(option) => option.fullName}
                                onChange={(_, newValue) => {
                                    if (!newValue)
                                        setselectedProvince(getEmptySelected());
                                    else
                                        setselectedProvince(newValue);
                                    setProject({
                                        ...project,
                                        province: newValue,
                                        city: ""
                                    })
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Province'
                                        margin="normal"
                                        variant="outlined"
                                    />
                                )}
                                disabled={selectedCountry._id === ""}
                            />
                        </div>
                        <div className="col-4">
                            <Autocomplete
                                id='Cities'
                                options={cities.filter(item => item.provincia === selectedProvince._id)}
                                value={project.city || null}
                                getOptionLabel={(option) => option.fullName}
                                onChange={(_, newValue) => {
                                    setProject({
                                        ...project,
                                        city: newValue
                                    })
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='City'
                                        margin="normal"
                                        variant="outlined"
                                    />
                                )}
                                disabled={selectedProvince._id === ""}
                            />
                        </div>
                    </div>


                    <div className="d-flex align-item-center">
                        <div className="col-4">
                            <Autocomplete
                                id='Technologies'
                                options={technologies}
                                value={project.technology || []}
                                getOptionLabel={(option) => option.title}
                                onChange={(_, newValue) => {
                                    setProject({
                                        ...project,
                                        technology: newValue
                                    })
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Technology'
                                        margin="normal"
                                        variant="outlined"
                                    />
                                )}
                                multiple
                            />
                        </div>
                        <div className="col-4">
                            <Autocomplete
                                id='Development status'
                                options={developmentStates}
                                value={project.developmentStatus || null}
                                getOptionLabel={(option) => option.title}
                                onChange={(_, newValue) => {
                                    setProject({
                                        ...project,
                                        developmentStatus: newValue
                                    })
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Development status'
                                        margin="normal"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </div>
                        <div className="col-4">
                            <TextField
                                label="SizeMW"
                                value={project.sizeMW}
                                onChange={handleChange("sizeMW")}
                                type="number"
                                fullWidth
                                margin="normal"
                                variant="outlined"
                            />
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Button
                onClick={() => history.push("/projects")}
                variant="outlined"
                style={{ marginRight: 20 }}
            >
                Back
            </Button>

            <Button
                onClick={() => saveProject()}
                variant="outlined"
                color="primary"
                style={{ marginRight: 20 }}
            >
                Save Project
            </Button>
            {
                projectId && (
                    <>
                        <MuiThemeProvider theme={theme}>
                            <Button
                                onClick={() => setOpenConfirmDialog(true)}
                                variant="outlined"
                                color="secondary"
                            >
                                Delete Project
                            </Button>
                        </MuiThemeProvider>

                        <ConfirmDialog
                            title="Are you sure you want to delete this project?"
                            open={openConfirmDialog}
                            setOpen={setOpenConfirmDialog}
                            onConfirm={() => {
                                deleteProject(projectId)
                                    .then((res) => {
                                        alertSuccess({
                                            title: "Deleted!",
                                            customMessage: "Project deleted successfully",
                                        });
                                        history.push("/projects");
                                    })
                                    .catch((error) => {
                                        alertError({
                                            error: error,
                                            customMessage: "Could not delete project.",
                                        });
                                    });
                            }}
                        />
                    </>
                )
            }
        </>
    );
}